/* eslint-disable no-console */
import React, { useCallback } from "react";
import clsx from "clsx";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import {
  HvInput,
  HvTextArea,
  HvGrid,
  HvButton,
  HvTooltip,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import { CameraStatuses, CameraTypes, useTimeSettings } from "lib/api";

import { Duplicate, Info } from "@hitachivantara/uikit-react-icons";
import CameraStatusIcon from "components/camera/CameraStatusIcon";
import { getFormattedDateTimeWithTZ } from "lib/utils/datetime";
import moment from "moment";
import useStyles from "./styles";

interface DetailsProps {
  mode?: "add" | "edit";
}

const Details: React.FC<DetailsProps> = ({ mode = "add" }) => {
  const { t } = useTranslation("cameraAddEdit");
  const classes = useStyles();

  const {
    values,
    setFieldTouched,
    setFieldValue,
    status,
    setStatus,
    errors,
    touched,
  } = useFormikContext<CameraFormData>();
  const { timezone } = useTimeSettings();
  const { serverSideErrors = {} } = status;

  const parseStatus = (field: keyof CameraFormData) =>
    serverSideErrors[field] || (errors[field] && touched[field])
      ? "invalid"
      : "valid";

  const parseStatusMessage = (field: keyof CameraFormData) => {
    if (serverSideErrors[field]) {
      return serverSideErrors[field];
    }

    return errors[field] && touched[field] ? errors[field] : "";
  };

  const setValueOnForm = useCallback(
    (property: string, selection: unknown) => {
      setFieldValue(property, selection);
      // clearServerSideErrors
      setStatus({});
    },
    [setFieldValue, setStatus]
  );

  return (
    <HvGrid
      className={clsx(classes.root, classes.fields)}
      container
      item
      xs={12}
    >
      <HvGrid xs={12} md={6} className={classes.gridPaddingOverRide}>
        <HvInput
          className={classes.input}
          readOnly={values.type === CameraTypes.VMS}
          name="name"
          label={t("details.fields.name")}
          placeholder={t("details.fields.namePlaceholder")}
          inputProps={{
            autoComplete: "off",
          }}
          required
          value={values.name}
          status={parseStatus("name")}
          statusMessage={parseStatusMessage("name")}
          onBlur={() => setFieldTouched("name", true, false)}
          onChange={(evt, value) => {
            setValueOnForm("name", value);
          }}
        />
      </HvGrid>
      <HvGrid className={classes.gridPaddingOverRide} xs={12} md={3}>
        <HvTypography>
          {t("details.fields.status")}
          <HvTooltip
            title={
              <HvTypography>{t("details.fields.statusTooltip")}</HvTypography>
            }
          >
            <HvButton icon aria-label="info">
              <Info />
            </HvButton>
          </HvTooltip>
        </HvTypography>
        {mode === "add" && (
          <HvTypography>
            <span className={classes.noIcon}>—</span>
          </HvTypography>
        )}
        {mode !== "add" && (
          <CameraStatusIcon
            status={values.status ?? CameraStatuses.StatusNotAvailable}
          />
        )}
      </HvGrid>
      <HvGrid className={classes.gridPaddingOverRide} xs={12} md={3}>
        <HvTypography>
          {t("details.fields.ptz")}
          <HvTooltip
            title={
              <HvTypography>{t("details.fields.ptzTooltip")}</HvTypography>
            }
          >
            <HvButton icon aria-label="info">
              <Info />
            </HvButton>
          </HvTooltip>
        </HvTypography>
        {mode === "add" && (
          <HvTypography>
            <span className={classes.noIcon}>—</span>
          </HvTypography>
        )}
        {mode !== "add" &&
          (values.ptz ? (
            <HvTypography>
              <span>{t("details.fields.ptzYes")}</span>
            </HvTypography>
          ) : (
            <HvTypography>
              <span>{t("details.fields.ptzNo")}</span>
            </HvTypography>
          ))}
      </HvGrid>
      <HvGrid className={classes.gridPaddingOverRide} xs={12} md={6}>
        <HvInput
          className={classes.input}
          readOnly
          value={values.id}
          placeholder={t("details.fields.idPlaceholder")}
          label={t("details.fields.id")}
          endAdornment={
            <HvButton icon aria-label="duplicate">
              <Duplicate
                onClick={() => {
                  if (navigator.clipboard === undefined) {
                    console.log("clipboard is undefined");
                  } else {
                    navigator.clipboard.writeText(values.id ?? "").then(
                      () => {
                        console.log("Copied to clipboard successfully!");
                      },
                      () => {
                        console.error("Unable to write to clipboard. :-(");
                      }
                    );
                  }
                }}
              />
            </HvButton>
          }
        />
      </HvGrid>
      <HvGrid className={classes.gridPaddingOverRide} xs={12} md={6}>
        <HvInput
          className={classes.input}
          readOnly
          value={
            values.updateTime
              ? getFormattedDateTimeWithTZ(
                  moment(values.updateTime as Date),
                  timezone
                )
              : ""
          }
          placeholder={t("details.fields.lastUpdateTimePlaceholder")}
          label={t("details.fields.lastUpdateTime")}
        />
      </HvGrid>
      {values.type === CameraTypes.VMS && (
        <>
          <HvGrid className={classes.gridPaddingOverRide} xs={12} md={6}>
            <HvInput
              className={classes.input}
              readOnly
              value={values.sourceId}
              placeholder={t("details.fields.idSourcePlaceholder")}
              label={t("details.fields.idSource")}
              endAdornment={
                <HvButton icon aria-label="duplicate">
                  <Duplicate
                    onClick={() => {
                      if (navigator.clipboard === undefined) {
                        console.log("clipboard is undefined");
                      } else {
                        navigator.clipboard
                          .writeText(values.sourceId ?? "")
                          .then(
                            () => {
                              console.log("Copied to clipboard successfully!");
                            },
                            () => {
                              console.error(
                                "Unable to write to clipboard. :-("
                              );
                            }
                          );
                      }
                    }}
                  />
                </HvButton>
              }
            />
          </HvGrid>
          <HvGrid item xs={12} md={6} />
        </>
      )}
      <HvGrid className={classes.gridPaddingOverRide} xs={12} md={12}>
        <HvTextArea
          className={classes.textAreaInput}
          value={values.description}
          onBlur={() => setFieldTouched("description", true, false)}
          onChange={(evt, value) => {
            setValueOnForm("description", value);
          }}
          placeholder={t("details.fields.descriptionPlaceholder")}
          label={t("details.fields.description")}
          rows={5}
        />
      </HvGrid>
    </HvGrid>
  );
};

export default Details;
